import { FC } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '../../../utils/cn';

export interface BenefitItemProps {
  /**
   * Lucide icon component to display
   */
  icon: LucideIcon;
  /**
   * Short title for the benefit (2-4 words)
   */
  title: string;
  /**
   * Description of the benefit (1-2 sentences)
   */
  description: string;
  /**
   * Optional additional className
   */
  className?: string;
}

/**
 * Individual benefit item component displaying an icon, title, and description.
 * Used within BenefitsSection to showcase massage therapy benefits.
 *
 * @example
 * <BenefitItem
 *   icon={Award}
 *   title="Licensed Therapists"
 *   description="All our therapists are certified professionals."
 * />
 */
export const BenefitItem: FC<BenefitItemProps> = ({
  icon: Icon,
  title,
  description,
  className,
}) => {
  return (
    <div
      className={cn(
        'group flex flex-col items-center text-center p-6',
        'rounded-xl bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900/30',
        'transition-all duration-300 ease-in-out',
        'hover:shadow-md dark:hover:shadow-slate-900/50 hover:-translate-y-1',
        className
      )}
    >
      {/* Icon container */}
      <div
        className={cn(
          'mb-4 flex h-14 w-14 items-center justify-center',
          'rounded-full bg-primary-50 dark:bg-primary-900/50',
          'transition-colors duration-300',
          'group-hover:bg-primary-100 dark:group-hover:bg-primary-900/70'
        )}
      >
        <Icon
          className={cn(
            'h-7 w-7 text-primary-600 dark:text-primary-400',
            'transition-transform duration-300',
            'group-hover:scale-110'
          )}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      </div>

      {/* Title */}
      <h3 className="mb-2 font-serif text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h3>

      {/* Description */}
      <p className="text-sm leading-relaxed text-gray-600 dark:text-slate-400">{description}</p>
    </div>
  );
};

BenefitItem.displayName = 'BenefitItem';
